import { treeStore, type EditorAttachmentState } from '@/msgdeskRuntime/state'
import { getMsgDeskRuntime } from '../runtime/MsgDeskRuntime'
import { setEditorMoreSetting, syncEditorRoughModel } from './EditorRoughFacade'

const MSGDESK_MODEL_API_PATH = '/api/msgdesk/model'
let uploadSequence = 0

type LegacyAttEntry = any[] & { __v2Error?: string; __v2PreviewSrc?: string }

function rough() {
  return getMsgDeskRuntime().facade.editorRough()
}

function noopJq() {
  return {
    height: () => noopJq(),
    hide: () => noopJq(),
    remove: () => noopJq(),
    text: () => noopJq(),
    toggle: () => noopJq(),
  }
}

function syncAttachments(reason: string) {
  treeStore.mutate.syncLegacyRuntime?.(reason)
  syncEditorRoughModel(reason)
}

function nextAttachmentId(attsArr: unknown[]): number {
  let id = attsArr.length
  while (attsArr[id]) id += 1
  return Math.max(id, 2)
}

function uploadId() {
  uploadSequence = (uploadSequence + 1) % 0x100000000
  return `${Date.now().toString(16).padStart(16, '0')}${uploadSequence.toString(16).padStart(16, '0')}`
}

function ensureAttsArr(): unknown[] {
  const runtime = rough()!
  const attsArr = runtime.atts_arr || (runtime.atts_arr = [0, false])
  if (!Array.isArray(attsArr)) runtime.atts_arr = [0, false]
  return runtime.atts_arr
}

function createRuntimeEntry(file: File, previewUrl: string, id: number, idForUpload: string): LegacyAttEntry {
  const entry = [
    id,
    noopJq(),
    noopJq(),
    [idForUpload],
    noopJq(),
    noopJq(),
    false,
    [
      [false, previewUrl],
      file.name,
      '',
      '',
    ],
  ] as LegacyAttEntry
  entry.__v2PreviewSrc = previewUrl
  return entry
}

function parseUploadResponse(text: string): unknown[] | null {
  try {
    const parsed = JSON.parse(text)
    return Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

function markUploadDone(entry: LegacyAttEntry, response: unknown[] | null, previewUrl: string) {
  const uploadedPath = response?.[1] == null ? previewUrl : String(response[1])
  entry[3] = false
  entry[6] = true
  entry.__v2PreviewSrc = previewUrl
  entry[7][0] = [false, uploadedPath]
}

function markUploadFailed(entry: LegacyAttEntry, message: string) {
  entry[3] = false
  entry.__v2Error = message
  entry[6] = false
}

export function getEditorAttachments(): EditorAttachmentState[] {
  return treeStore.getState().templateData.editorAttachments || []
}

export function toggleAttachmentActive(attachment: EditorAttachmentState, active: boolean) {
  const id = Number(attachment.id)
  if (!(id > 0)) return

  const attsArr = rough()?.atts_arr
  const entry = Array.isArray(attsArr) ? attsArr[id] : null
  if (!Array.isArray(entry)) return

  entry[6] = active
  syncAttachments('v2-attachment-toggle')
}

export async function uploadAttachment(file: File): Promise<void> {
  const runtime = rough()
  if (!runtime || !file) return

  const attsArr = ensureAttsArr()
  const id = nextAttachmentId(attsArr)
  const idForUpload = uploadId()
  const previewUrl = getMsgDeskRuntime().createObjectURL(file)
  const entry = createRuntimeEntry(file, previewUrl, id, idForUpload)

  attsArr[id] = entry
  attsArr[0] = Number(attsArr[0] || 0) + 1
  syncAttachments('v2-attachment-upload-start')

  const form = new FormData()
  form.append('action', 'upload_att')
  form.append('id', idForUpload)
  form.append('title', `att title ${idForUpload}`)
  form.append('txt', `att description ${idForUpload}`)
  form.append('is_avatar', '0')
  form.append('attach_file', file, file.name)

  try {
    const response = await fetch(`${MSGDESK_MODEL_API_PATH}?X-Progress-ID=${encodeURIComponent(idForUpload)}`, {
      method: 'POST',
      body: form,
    })
    const text = await response.text()
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    markUploadDone(entry, parseUploadResponse(text), previewUrl)
  } catch (error) {
    markUploadFailed(entry, error instanceof Error ? error.message : String(error))
  } finally {
    syncAttachments('v2-attachment-upload-complete')
  }
}

export function currentAvatarSrc(): string {
  const attsArr = rough()?.atts_arr
  const entry = Array.isArray(attsArr) ? attsArr[1] as LegacyAttEntry | undefined : undefined
  const data = Array.isArray(entry?.[7]) ? entry?.[7] : []
  const paths = Array.isArray(data?.[0]) ? data[0] : []
  const msgData = getMsgDeskRuntime().facade.msgData()
  return entry?.__v2PreviewSrc || String(paths?.[1] || paths?.[0] || msgData?.avatar_path || 'imgs/avatar.gif')
}

export async function uploadAvatar(file: File): Promise<void> {
  const runtime = rough()
  if (!runtime || !file) return

  const attsArr = ensureAttsArr()
  const idForUpload = uploadId()
  const previewUrl = getMsgDeskRuntime().createObjectURL(file)
  const entry = createRuntimeEntry(file, previewUrl, 1, idForUpload)

  attsArr[1] = entry
  setEditorMoreSetting('is_avatar', true, 'v2-avatar-upload-enable')
  syncAttachments('v2-avatar-upload-start')

  const form = new FormData()
  form.append('action', 'upload_att')
  form.append('id', idForUpload)
  form.append('title', `att title ${idForUpload}`)
  form.append('txt', `att description ${idForUpload}`)
  form.append('is_avatar', '1')
  form.append('attach_avatar', file, file.name)

  try {
    const response = await fetch(`${MSGDESK_MODEL_API_PATH}?X-Progress-ID=${encodeURIComponent(idForUpload)}`, {
      method: 'POST',
      body: form,
    })
    const text = await response.text()
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    markUploadDone(entry, parseUploadResponse(text), previewUrl)
  } catch (error) {
    markUploadFailed(entry, error instanceof Error ? error.message : String(error))
  } finally {
    syncAttachments('v2-avatar-upload-complete')
  }
}
